import React, { useState, useEffect } from 'react'
import { Button } from 'reactstrap'
import has from 'lodash/has'
import intl from 'react-intl-universal'
import { Statuses, useEmbed } from '~/reactapp/shared/use-embed'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cx = require('./styles.scss')

export interface IFrameViewerProps {
  /** Passed directly to iframe src attribute */
  source: string
  /** How long to wait for iframe to load before considering a load "failure" */
  timeout?: number
  /**
   * Payload data to the iframe viewer
   */
  data?: unknown
  /** If true, IFrameViewer do not render a focus capturing overlay */
  interactionsEnabled: boolean
}

interface ViewEmbedMessage {
  data: {
    height: string
  }
}

const defaultHeight = 200

export function IFrameViewer(props: IFrameViewerProps) {
  const [height, setHeight] = useState(defaultHeight) // set reasonable height so if none is provided its still usable with scrollbars
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  const { messageData, retry, status, unsubscribe, url } = useEmbed({
    timeoutMs: props.timeout,
    baseUrl: props.source,
    data: props.data,
    iframeElem: iframeRef,
  })

  useEffect(() => {
    if (has(messageData, 'data.height')) {
      setHeight(Number.parseInt((messageData as ViewEmbedMessage).data.height))
      // This component stays mounted, but doesn't care about future message events
      // so we manually stop listening.
      unsubscribe()
    }
    // Reset height to the default.
    // Otherwise view plugin might get more and more scroll height and will report back
    // higher height during editing cycles
    if (status === Statuses.Loading) {
      setHeight(defaultHeight)
    }
  }, [messageData, unsubscribe, status])

  return (
    <>
      {status === Statuses.Timeout ? (
        <div className={`${cx.timeoutRetry} ${cx.noView}`}>
          <p>{intl.get('blocks.customEmbed.ui.timeout')}</p>
          <Button color="primary" onClick={retry}>
            {intl.get('blocks.customEmbed.ui.retry')}
          </Button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {status === Statuses.Loading ? (
            <div className={cx.loading}>
              <span>{intl.get('blocks.customEmbed.ui.loading')}</span>
            </div>
          ) : null}
          <iframe
            ref={iframeRef}
            data-testid="iframe-viewer"
            style={{
              border: 'none',
              height,
              overflowY: 'auto',
              ...(status === Statuses.Loading && { visibility: 'hidden' }),
            }}
            title={props.source}
            width="100%"
            src={url}
          />
          {/*
            Events originating from the iframe do not bubble higher than the iframe root (for security).
            Because of this, clicking inside the block does not bubble the click up to the editor preventing the block
            from receiving focus. Overlay div is here to capture events so the editor will focus this block
           */}
          {props.interactionsEnabled ? null : (
            <div
              className={`${cx.iframeOverlay} ${cx.previewNonDraggableIframe}`}
              data-testid="iframe-overlay"
            />
          )}
        </div>
      )}
    </>
  )
}
